import { useState, useEffect, useCallback } from "react";
import trpc from "@/shared/utils/trpc";

interface UseFileUrlReturn {
  url: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch a presigned URL for a file stored in S3.
 * The URL is fetched only once and cached until it expires.
 *
 * @param fileId - The ID of the file to get the URL for
 * @returns Object with url, loading state, error, and refetch function
 */
export function useFileUrl(fileId: string | null | undefined): UseFileUrlReturn {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUrl = useCallback(async () => {
    if (!fileId) {
      setUrl(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await trpc.file.getFileUrl.query({ fileId });
      setUrl(result.url);
    } catch (err: any) {
      console.error("Failed to fetch file URL:", err);
      setError(err.message || "Failed to fetch file URL");
      setUrl(null);
    } finally {
      setLoading(false);
    }
  }, [fileId]);

  useEffect(() => {
    fetchUrl();
  }, [fetchUrl]);

  return { url, loading, error, refetch: fetchUrl };
}

/**
 * Fetches presigned URL for a file.
 * Use this for one-off URL fetches (e.g., in event handlers).
 *
 * @param fileId - The ID of the file to get the URL for
 * @returns Promise resolving to the presigned URL or null
 */
export async function getFileUrl(fileId: string, download?: boolean): Promise<string | null> {
  try {
    const result = await trpc.file.getFileUrl.query({ fileId, download });
    return result.url;
  } catch (err: any) {
    console.error("Failed to fetch file URL:", err);
    return null;
  }
}

/**
 * Constructs a file URL for display.
 * For S3 files, fetches presigned URL.
 * For external URLs (Google Drive, etc.), returns the URL directly.
 *
 * @param file - File object with id, url, and path properties
 * @returns Promise resolving to the displayable URL
 */
export async function getDisplayUrl(file: { id: string; url?: string | null; path?: string }): Promise<string | null> {
  // If file has a URL (external link like Google Drive), return it directly
  if (file.url) {
    return file.url;
  }

  // Otherwise, fetch presigned URL from S3
  return getFileUrl(file.id);
}

/**
 * Fetches presigned URLs for multiple files in a batch.
 *
 * @param fileIds - Array of file IDs
 * @returns Promise resolving to a map of fileId -> url
 */
export async function getFileUrls(fileIds: string[], download?: boolean): Promise<Record<string, string>> {
  try {
    if (fileIds.length === 0) return {};
    const result = await trpc.file.getFileUrls.query({ fileIds, download });
    return result.urls;
  } catch (err: any) {
    console.error("Failed to fetch file URLs batch:", err);
    return {};
  }
}
